import React, { useState } from 'react';

import { ISensorFusionSubstageInstanceLookupDto } from '@scale/sensor-fusion-shared';
import { SubstageInstanceStatus } from '@scale/shared-types';
import { RemoAdminTool } from '@scale/shared/types/remoAdmin';

import RemotasksExternalLink from 'react-scale/corp/components/UI/RemotasksExternalLink';
import { Separator } from 'react-scale/components/design-system';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';
import ReassignSubstageClaimModal from 'react-scale/corp/components/SFU/TaskTracker/components/ReassignSubstageClaimModal';
import SubmitSubstageModal from 'react-scale/corp/components/SFU/TaskTracker/components/SubmitSubstageModal';

interface SubstageInstanceActionsProps {
  substageInstance: ISensorFusionSubstageInstanceLookupDto;
}

const SubstageInstanceActions = ({ substageInstance }: SubstageInstanceActionsProps) => {
  const { isRemoAdmin, currentPersona } = useRemoAdminCtx();

  const [isReassignClaimModalOpen, setIsReassignClaimModalOpen] = useState<boolean>(false);
  const [isSubmitSubstageModalOpen, setIsSubmitSubstageModalOpen] = useState<boolean>(false);
  const humanNodeId = substageInstance.humanNode?.id;
  const baseProject = (substageInstance.project as unknown as { baseProject?: string })
    ?.baseProject;

  const toolUrl = `${isRemoAdmin ? '/remoadmin/tools' : '/corp'}`;
  const hasTaskTrackerAccess = currentPersona?.tools?.includes(RemoAdminTool.SFUTaskTracker);

  return (
    <div className="border-solid border border-neutral-200 px-4 py-4 my-4 w-full overflow-wrap-anywhere">
      <h4 className="font-bold text-lg">Actions</h4>
      <div className="py-1 flex flex-col space-y-2 text-info-700 font-medium cursor-pointer">
        {humanNodeId && (
          <div className="py-1 flex flex-col space-y-2 text-info-700 font-medium cursor-pointer">
            <RemotasksExternalLink path="/lidarlite" query={{ sceneId: humanNodeId, readOnly: 1 }}>
              View as Tasker (read-only)
            </RemotasksExternalLink>
            <RemotasksExternalLink
              path="/tasks"
              query={{ pipelineV3HumanNodeId: humanNodeId, forceClaim: 1 }}
            >
              View Task with Claim
            </RemotasksExternalLink>
            <RemotasksExternalLink
              path="/lidarlite"
              query={{ sceneId: humanNodeId, entrypoint: 'debug' }}
            >
              View Scene in LL Debug Viewer
            </RemotasksExternalLink>
            <Separator />
          </div>
        )}

        {baseProject && (!isRemoAdmin || hasTaskTrackerAccess) && (
          <a href={`${toolUrl}/sfu_task_tracker/${baseProject}`}>View in SFU Task Tracker</a>
        )}

        {substageInstance.status !== SubstageInstanceStatus.Completed &&
          substageInstance.status !== SubstageInstanceStatus.NotStarted &&
          (!isRemoAdmin || hasTaskTrackerAccess) && (
            <div>
              <text onClick={() => setIsReassignClaimModalOpen(true)}>Reassign Claim</text>
              <ReassignSubstageClaimModal
                open={isReassignClaimModalOpen}
                currentWorkerEmail={substageInstance.workerInfo?.email}
                substageInstanceId={substageInstance._id}
                reviewLevel={substageInstance?.reviewLevel}
                setOpen={setIsReassignClaimModalOpen}
                title={'Reassign Claim for Substage Instance'}
              />
            </div>
          )}

        {!isRemoAdmin && (
          <>
            <a href={`${toolUrl}/sfu_redo_recall`}>Redo Substage</a>

            <text onClick={() => setIsSubmitSubstageModalOpen(true)}>Complete Substage</text>

            <SubmitSubstageModal
              open={isSubmitSubstageModalOpen}
              setOpen={setIsSubmitSubstageModalOpen}
              substageInstanceId={substageInstance._id}
              title={'Submit Substage Instance'}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default SubstageInstanceActions;
